import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Link2,
  MessageCircleQuestion,
  MessagesSquare,
  Megaphone,
  Target,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useAddSeoAiPrompts,
  useMarkSeoAiAction,
  useRunSeoAiAnswers,
  useSeoAiAnswersQuery,
  type ApiSeoAiAnswer,
  type SeoAnswerEngine,
} from '@/client/api/marketing/seo';
import { EmptyState } from './components/marketing-ui';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  NEUTRAL_SERIES,
  SITE_COLOR,
  SITE_LABEL,
  SeoError,
  SeoKpi,
  SeoKpiRow,
  SeoNote,
  SeoPanel,
  SeoTrend,
  ShareBar,
  StatusPill,
  TD,
  TH,
  TR,
} from './components/seo-ui';

const ENGINE_LABEL: Record<SeoAnswerEngine, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  google_ai_mode: 'Google AI Mode',
  perplexity: 'Perplexity',
};

/** Who one assistant named for one question. */
function Answer({ answer }: { answer: ApiSeoAiAnswer | undefined }) {
  if (!answer) return <span className="text-xs text-gray-400">No answer yet</span>;
  const others = Object.entries(answer.mentions)
    .filter(([brand, count]) => brand !== 'feelyfeely' && count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([brand]) => SITE_LABEL[brand] ?? brand);
  return (
    <div>
      <StatusPill
        status={answer.names_us ? 'pass' : 'fail'}
        label={answer.names_us ? (answer.cites_us ? 'Names and links us' : 'Names us') : 'Does not name us'}
      />
      <div className="mt-1 text-xs text-gray-600">{others.length ? `Names ${others.join(', ')}` : 'Names none we track'}</div>
    </div>
  );
}

/**
 * `/admin/seo/ai-answers` — what ChatGPT, Gemini and Google AI Mode tell someone who
 * asks where to buy, sell or rent in Ghana, and whether they name us
 * (ff-api service/seo_ai_answers.py).
 */
export default function SeoAiAnswersPage() {
  const { data, isLoading, error } = useSeoAiAnswersQuery();
  const add = useAddSeoAiPrompts();
  const run = useRunSeoAiAnswers();
  const mark = useMarkSeoAiAction();
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const summary = data?.summary;
  const plan = data?.plan;
  const engines = data?.engines ?? [];
  const topRival =
    (data?.brands ?? [])
      .filter((b) => b !== 'feelyfeely')
      .sort((a, b) => (summary?.share[b] ?? 0) - (summary?.share[a] ?? 0))[0] ?? 'jiji';

  const submit = () => {
    const prompts = draft
      .split('\n')
      .map((p) => p.trim())
      .filter(Boolean);
    if (prompts.length === 0) return;
    add.mutate({ prompts }, { onSuccess: () => setDraft('') });
  };

  return (
    <MarketingPageShell
      title="AI answers"
      description="What ChatGPT, Gemini and Google AI Mode answer when someone asks where to buy, sell or rent in Ghana, and who they name."
      crumbs={[{ label: 'SEO', href: '/admin/seo' }, { label: 'AI answers' }]}
      icon={MessagesSquare}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => add.mutate({ seed: true })} disabled={add.isPending}>
            Add the standard questions
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => run.mutate({})}
            disabled={run.isPending || run.isSuccess || !summary?.prompts}
          >
            {run.isSuccess ? 'Queued: results in about 10 minutes' : 'Ask the assistants now'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <SeoError>Could not load AI answers.</SeoError>}
        {(add.isError || run.isError || mark.isError) &&<SeoError>That did not go through. Try again.</SeoError>}

        <SeoKpiRow>
          <SeoKpi
            label="Answers that name us"
            value={summary ? `${summary.share.feelyfeely ?? 0}%` : undefined}
            icon={Megaphone}
            tone={summary && summary.answers > 0 && !summary.share.feelyfeely ? 'bad' : 'neutral'}
            hint={summary ? `Of ${summary.answers} answers` : undefined}
            loading={isLoading}
          />
          <SeoKpi
            label={`Answers that name ${SITE_LABEL[topRival] ?? 'a rival'}`}
            value={summary ? `${summary.share[topRival] ?? 0}%` : undefined}
            icon={Users}
            hint="The marketplace named most"
            loading={isLoading}
          />
          <SeoKpi
            label="Answers that link to us"
            value={summary?.cited}
            icon={Link2}
            hint="A citation, not only a mention"
            loading={isLoading}
          />
          <SeoKpi
            label="Questions asked"
            value={summary?.prompts}
            icon={MessageCircleQuestion}
            hint="Each put to every assistant weekly"
            loading={isLoading}
          />
        </SeoKpiRow>

        {plan && (
          <div className="grid gap-4 lg:grid-cols-3">
            <SeoPanel title="Goals" subtitle="In order; each is worth reaching before the next">
              <ol className="space-y-2.5 text-sm">
                {plan.goals.map((g) => {
                  const focus = g.id === plan.focus;
                  const Icon = g.met ? CheckCircle2 : focus ? Target : Circle;
                  return (
                    <li key={g.id} className={cn('flex gap-2', focus && 'rounded-md bg-indigo-50 p-2 -mx-2')}>
                      <Icon
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          g.met ? 'text-emerald-600' : focus ? 'text-indigo-600' : 'text-gray-300',
                        )}
                      />
                      <div className="min-w-0">
                        <p className={cn('font-medium', g.met ? 'text-gray-500' : 'text-gray-900')}>
                          {g.title}
                          {focus && <span className="ml-1.5 text-xs font-normal text-indigo-700">Current goal</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          <span className="tabular-nums">
                            {g.current}
                            {g.unit} of {g.target}
                            {g.unit}
                          </span>
                          {(focus || !g.met) && ` · ${g.why}`}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </SeoPanel>

            <SeoPanel
              className="lg:col-span-2"
              title="What to do next"
              subtitle="Three levers, read off the latest answers: be in the index each assistant searches, be named on the pages it quotes, and answer the question plainly on our own pages"
            >
              {plan.actions.length === 0 ? (
                <p className="py-2 text-sm text-gray-600">Nothing outstanding in the latest answers.</p>
              ) : (
                <ol className="divide-y divide-gray-100 text-sm">
                  {plan.actions.map((a, i) => (
                    <li key={a.key} className="flex gap-3 py-2">
                      <span className="w-5 shrink-0 text-right font-semibold tabular-nums text-gray-400">{i + 1}</span>
                      <div className="flex-1">
                        <p className={a.done ? 'text-gray-500' : 'text-gray-900'}>{a.text}</p>
                        {a.done && (
                          <p className="mt-1 text-xs text-amber-800">
                            Done {a.done.at.slice(0, 10)}. Not reflected in the answers yet; assistants can take weeks
                            to pick a change up.
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1 self-start">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={mark.isPending}
                          onClick={() => mark.mutate({ key: a.key, done: !a.done, text: a.text })}
                        >
                          {a.done ? 'Undo' : 'Mark done'}
                        </Button>
                        {a.to && (
                          <Link
                            to={a.to}
                            className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
                          >
                            Open
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </SeoPanel>
          </div>
        )}

        {plan?.log && plan.log.length > 0 && (
          <SeoPanel
            title="Steps taken"
            subtitle="What was marked done, and our share of answers then and now. A step leaves the list once the answers no longer call for it."
            flush
          >
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={TH}>Done</th>
                  <th className={TH}>Step</th>
                  <th className={TH}>Answers naming us</th>
                  <th className={TH}>Status</th>
                </tr>
              </thead>
              <tbody>
                {plan.log.map((e) => (
                  <tr key={e.key} className={cn(TR, 'align-top')}>
                    <td className={cn(TD, 'whitespace-nowrap tabular-nums text-gray-600')}>{e.at.slice(0, 10)}</td>
                    <td className={cn(TD, 'max-w-xl text-gray-900')}>{e.text}</td>
                    <td className={cn(TD, 'whitespace-nowrap tabular-nums')}>
                      {e.share_then}% then, {e.share_now}% now
                    </td>
                    <td className={TD}>
                      <StatusPill
                        status={e.open ? 'warn' : 'pass'}
                        label={e.open ? 'Waiting on the answers' : 'No longer needed'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SeoPanel>
        )}

        {summary && summary.answers > 0 && (
          <div className="grid gap-4 lg:grid-cols-2">
            <SeoPanel title="Share of answers naming each marketplace" subtitle="Newest answer per question and assistant">
              {[...(data?.brands ?? [])]
                .sort((a, b) => (summary.share[b] ?? 0) - (summary.share[a] ?? 0))
                .map((brand) => (
                  <ShareBar
                    key={brand}
                    label={SITE_LABEL[brand] ?? brand}
                    value={summary.share[brand] ?? 0}
                    color={SITE_COLOR[brand] ?? NEUTRAL_SERIES}
                    emphasis={brand === 'feelyfeely'}
                  />
                ))}
            </SeoPanel>

            <SeoPanel title="Answers that name us" subtitle="By assistant now, and across all assistants per weekly check">
              <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                {engines.map((engine) => (
                  <span key={engine} className="text-gray-600">
                    {ENGINE_LABEL[engine]}{' '}
                    <span className="font-semibold tabular-nums text-gray-900">{summary.by_engine[engine] ?? 0}%</span>
                  </span>
                ))}
              </div>
              <SeoTrend
                height={150}
                data={data?.trend ?? []}
                series={[{ key: 'share', name: 'Answers naming us', color: SITE_COLOR.feelyfeely }]}
                format={(v) => `${v}%`}
              />
            </SeoPanel>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <label className="flex-1 text-sm text-gray-700">
            Add questions, one per line, phrased as a buyer or seller would ask an assistant
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              placeholder="Where can I buy a generator in Kumasi?"
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <Button size="sm" onClick={submit} disabled={add.isPending || !draft.trim()}>
            Add
          </Button>
        </div>

        {!isLoading && (data?.prompts.length ?? 0) === 0 ? (
          <EmptyState label="No questions yet. Add the standard questions, or write your own above." />
        ) : (
          <SeoPanel title="Question by question" subtitle="Click a question to read what each assistant said and the sites it drew on" flush>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={TH}>Question</th>
                  {engines.map((engine) => (
                    <th key={engine} className={TH}>
                      {ENGINE_LABEL[engine]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.prompts ?? []).map((p) => (
                  <Fragment key={p.prompt}>
                    <tr className={cn(TR, 'align-top')}>
                      <td className={cn(TD, 'max-w-sm')}>
                        <button
                          type="button"
                          onClick={() => setOpen(open === p.prompt ? null : p.prompt)}
                          className="text-left text-gray-900 hover:underline"
                        >
                          {p.prompt}
                        </button>
                      </td>
                      {engines.map((engine) => (
                        <td key={engine} className={TD}>
                          <Answer answer={p.answers[engine]} />
                        </td>
                      ))}
                    </tr>
                    {open === p.prompt && (
                      <tr className={cn(TR, 'bg-gray-50')}>
                        <td colSpan={engines.length + 1} className="space-y-2 px-4 py-3 text-xs leading-relaxed text-gray-700">
                          {engines.map((engine) => {
                            const a = p.answers[engine];
                            if (!a) return null;
                            return (
                              <div key={engine}>
                                <p>
                                  <span className="font-medium">
                                    {ENGINE_LABEL[engine]}, {a.day}:
                                  </span>{' '}
                                  {a.excerpt}
                                </p>
                                {a.sources.length > 0 && (
                                  <p className="text-gray-500">Sources: {a.sources.slice(0, 8).join(', ')}</p>
                                )}
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </SeoPanel>
        )}

        <SeoNote>
          Checked weekly. An assistant can answer the same question differently each time, so read the share across all
          questions rather than any single answer. Each answer is a paid lookup, so the list holds at most 30 questions.
        </SeoNote>
      </div>
    </MarketingPageShell>
  );
}
